/* eslint-disable react-hooks/exhaustive-deps */
import {useQuery} from "@tanstack/react-query"
import {Device} from "@twilio/voice-sdk"
import {handleError, toastInfo} from "helpers"
import {SocketEvent} from "helpers/socket"
import {useModel} from "hooks"
import {createContext, useCallback, useEffect, useState} from "react"
import {autoDialerService, coreSocket, zoomService} from "services"
import {AutoDialer} from "types/auto-dialer"
import {Settings} from "types/settings"
import {Zoom} from "types/zoom"
import {useMutation} from "@tanstack/react-query"

type ContextProps = {
    calling: boolean
    voiceProvider: Settings.DialerProvider
    startCall: (student?: DialerStudent, departmentId?: number) => void
    hangup: () => void
    activeStudent: DialerStudent | null
    setActiveStudent: (student: DialerStudent) => void
    activeDepartmentId: number | null
    setActiveDepartmentId: (departmentId: number) => void
    queuedStudents: DialerStudent[]
    setQueuedStudents: (students: DialerStudent[]) => void
    callStatus: string
    isConnected: boolean
    isAuto: boolean
    setIsAuto: (isAuto: boolean) => void
}

export type DialerStudent = {
    userId: number
    profileId: number
    fullName: string
    email: string
    phone: string
    photo?: string
    dialerId?: number
}

export const VoiceCallContext = createContext<ContextProps>({} as ContextProps)

type VoiceCallProviderProps = {
    children: React.ReactNode
}

let device: Device | null = null

export const VoiceCallProvider: React.FC<VoiceCallProviderProps> = ({children}) => {
    // information
    const [activeStudent, setActiveStudent] = useState<DialerStudent | undefined>(undefined)
    const [queuedStudents, setQueuedStudents] = useState<DialerStudent[]>([])
    const [activeDepartmentId, setActiveDepartmentId] = useState<number | undefined>()
    const [isAuto, setIsAuto] = useState(false)

    // status
    const [calling, setCalling] = useState(false)
    const [deviceStatus, setDeviceStatus] = useState<string>()
    const [callStatus, setCallStatus] = useState<string>()

    // provider
    const [zoomAccount, setZoomAccount] = useState<Zoom.ZoomAccount | undefined>()

    const model = useModel()
    const voiceProvider = model.clientSetting.dialerProvider
    const slug = model.slug
    const staffProfileId = model.profileId
    const isConnected = callStatus === AutoDialer.DialerStatus.Connected

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (calling) {
                e.preventDefault()
                return "You have an active call. Are you sure you want to leave?"
            }
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload)
        }
    }, [calling])

    useQuery({
        queryKey: ["twilio-token"],
        queryFn: () => autoDialerService.getTwilioToken(),
        enabled: voiceProvider === Settings.DialerProvider.Twilio,
        refetchOnMount: true,
        onSuccess: (data) => {
            device = new Device(data.token)

            device.on("ready", () => setDeviceStatus("ready"))
            device.on("connect", () => setDeviceStatus("connected"))
            device.on("disconnect", () => {
                setDeviceStatus("disconnected")
                setCalling(false)
            })

            device.register()
        }
    })

    useQuery({
        queryKey: ["zoom-account"],
        queryFn: () => zoomService.getMyZoomAccount(),
        enabled: voiceProvider === Settings.DialerProvider.Zoom,
        refetchOnMount: true,
        onSuccess: (data) => {
            setZoomAccount(data)
        }
    })

    const startCall = useCallback(
        (student?: DialerStudent, _departmentId?: number) => {
            if (!student && !activeStudent) return
            let studentInfo = activeStudent
            let departmentId = activeDepartmentId
            if (student) {
                setActiveStudent(student)
                studentInfo = student
            }
            if (_departmentId) {
                setActiveDepartmentId(_departmentId)
                departmentId = _departmentId
            }

            if (voiceProvider === Settings.DialerProvider.Twilio) {
                const twilioPhoneNumber = model.smsAllPhoneNumbers.find(
                    (phone) => phone.departmentForDialer?.departmentId === departmentId
                )
                if (!twilioPhoneNumber) {
                    toastInfo("You haven't set up a phone number for this department")
                    return
                }

                let params: any = {
                    from: twilioPhoneNumber.phone,
                    to: studentInfo.phone,
                    staffProfileId: staffProfileId.toString(),
                    studentProfileId: studentInfo.profileId.toString(),
                    slug,
                    departmentId: departmentId ? departmentId.toString() : undefined
                }
                if (studentInfo.dialerId) {
                    params.dialerId = studentInfo.dialerId.toString()
                }
                device.connect({params})
            }
            if (voiceProvider === Settings.DialerProvider.Zoom) {
                if (!zoomAccount) {
                    toastInfo(
                        "You have not connected your Zoom account yet",
                        "Now we will redirect you to connect your account. After that, you can start the call."
                    )
                    setTimeout(() => {
                        grantZoomAccountMutation.mutate()
                    }, 1000)
                    return
                }
                window.open(`zoomphonecall://${studentInfo.phone}`, "_blank", "noopener,noreferrer")
            }
            setCalling(true)
            setCallStatus(AutoDialer.DialerStatus.Initial)
        },
        [activeStudent, voiceProvider, activeDepartmentId, zoomAccount]
    )

    const onEndedCall = useCallback(() => {
        if (isAuto) {
            setTimeout(() => {
                const [nextStudent, ...rest] = queuedStudents
                setQueuedStudents(rest)
                if (rest.length <= 0) {
                    setIsAuto(false)
                }
                startCall(nextStudent, activeDepartmentId)
            }, 10000)
        } else {
            setCalling(false)
        }
    }, [isAuto, queuedStudents, activeDepartmentId, startCall])

    useEffect(() => {
        if (callStatus === AutoDialer.DialerStatus.Ended) {
            onEndedCall()
        }
    }, [callStatus, onEndedCall])

    const handleDialerStatus = useCallback(
        (payload: AutoDialer.DialerSocketPayload) => {
            // only process for current profile logged in
            if (staffProfileId === payload.profileId && activeStudent?.profileId === payload.studentProfileId) {
                const {status: dialerStatus} = payload
                setCallStatus(dialerStatus)
            }
        },
        [staffProfileId, activeStudent]
    )

    useEffect(() => {
        ;(async function listenDialerSocket() {
            coreSocket.on(SocketEvent.DialerUpdated, handleDialerStatus)
            return () => {
                coreSocket.off(SocketEvent.DialerUpdated, handleDialerStatus)
            }
        })()
    }, [handleDialerStatus])

    const hangup = useCallback(() => {
        device?.disconnectAll()
        setCallStatus(AutoDialer.DialerStatus.Ended)
    }, [device])

    const grantZoomAccountMutation = useMutation({
        mutationFn: () =>
            zoomService.grantAccount({
                backUrl: window.location.href
            }),
        onSuccess: (authorizedData) => {
            if (authorizedData && authorizedData.redirect === true) {
                window.location.href = authorizedData.redirectUri
            }
        },
        onError: (error) => {
            handleError(error)
        }
    })

    return (
        <VoiceCallContext.Provider
            value={{
                calling,
                voiceProvider,
                startCall,
                hangup,
                activeStudent,
                setActiveStudent,
                activeDepartmentId,
                setActiveDepartmentId,
                queuedStudents,
                setQueuedStudents,
                callStatus,
                isConnected,
                isAuto,
                setIsAuto
            }}>
            {children}
        </VoiceCallContext.Provider>
    )
}
